import { h } from 'preact';
import React from 'react';

import injectQueryParams from '../../injectQueryParams';
import Children from './Children';
import { NoticeSettings } from './NoticeSettings';
import noticeStarter from './noticeStarter';
import {
  getBrowserLanguage,
  addStylesToHead,
  convertClassName,
  isLangSupported
} from '../../util';
import { SafeArea } from '../../Components/SafeArea/SafeArea';

import './notice.css';
import { DefaultLanguageContext, RefetchStacksContext } from '../../contexts';

const msgV2Origins = ['https://notice.sp-prod.net', 'https://notice.sp-stage.net',];

export class Notice extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      choices: [],
      components: props.messageId ? [{}] : noticeStarter,
      stacks: null,
      siteId: null,
      current: [0],
      loading: true,
      actions: [],
      width: 0,
      height: 0,
      devOpts: { hideRowColumnBorder: false, },
      highlight: null,
      stacksLanguage: null,
    };
  }

  componentDidMount = () => {
    const { messageId, preloadMsg } = this.props;

    window.addEventListener('keydown', this.handleFirstTab);
    window.addEventListener('resize', this.updateWindowDimensions);
    if (preloadMsg) {
      // important we only add this when preloadMsg is true
      // when preloading we also will apply extra security to prevent XSS attacks
      window.addEventListener('message', this.loadPreloadedMsg, false);
      window.parent.postMessage({ name: 'sp.readyForPreload' }, '*');
    };

    if (messageId && !preloadMsg) {
      fetch(`${this.props.mmsOrigin}/message?message_id=${messageId}`, { insecure: true })
        .then(r => r.json())
        .then(this.loadMessage.bind(this));
    } else if (this.props.dev || this.props.tiny || this.props.mini || this.props.full) {
      this.setState({ loading: false });
      window.addEventListener('message', this.handleDev, false);
    }
  }

  handleFirstTab = (e) => {
    if (e.keyCode === 9) {
      document.body.classList.add('user-is-tabbing');
      window.removeEventListener('keydown', this.handleFirstTab);
    }
  }

  loadMessage(data) {
    try {
      const parsedComponents = (typeof data.message_json === 'string')
        ? JSON.parse(data.message_json)
        : data.message_json;
      const components = Array.isArray(parsedComponents) ? parsedComponents : [parsedComponents];
      if (components[0].css) addStylesToHead(components[0].css);
      this.setState({
        components,
        choices: data.message_choice || [],
        type: components[0].settings.type,
        siteId: data.site_id || null,
        stacks: data.categories || null,
        stacksLanguage: data.language ? data.language.toUpperCase() : null,
        loading: false
      }, () => {
        window.addEventListener('message', this.handleMessages, false);
        window.parent.postMessage({ name: 'sp.showMessage', actions: [], settings: components[0].settings }, '*');
        this.updateWindowDimensions();
        this.setState({ actions: [] });
      });
    } catch (err) {
      throw Error(`Could not parse message data: ${err}`);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleDev);
    window.removeEventListener('message', this.handleMessages);
    window.removeEventListener('message', this.loadPreloadedMsg);
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  updateWindowDimensions = () => {
    if (!this.state.loading && (this.state.type === 'top' || this.state.type === 'bottom' || this.state.type === 'inline')) {
      const noticeComponent = this.state.components ? this.state.components[0] : {};
      const Settings = new NoticeSettings(noticeComponent.settings || {});
      window.parent.postMessage({
        name: 'sp.resizeMessage',
        type: this.state.type,
        height: this.noticeRef ? this.noticeRef.clientHeight + ((Settings.border.borderWidth || 0) * 2) : 0,
        heightSettings: Settings.height,
        showVeil: Settings.showVeil,
      }, '*');
    }
    this.setState({ width: window.innerWidth, height: window.innerHeight });
  }

  makeChoice = (choiceId, consentLanguage, firstLayerSE) => {
    if (firstLayerSE === true) return this.makeCategoryChoice(consentLanguage);
    let choice = this.state.choices.find(c => c.button_text === choiceId);
    const noticeComponent = this.state.components ? this.state.components[0] : {};
    // If no choice found, I want to go ahead and dismiss the message
    if (!choice) choice = this.state.choices.find(c => c.type === 15);

    const actions = this.state.actions.concat({
      type: "choice",
      data: choice
    });
    this.setState({ actions }, () => {
      if (choice.type) {
        window.parent.postMessage(
          {
            name: 'sp.hideMessage',
            actions,
            settings: noticeComponent.settings || {},
            consentLanguage,
          }, '*',
        );
        this.setState({ actions: [] });
      }
    })
  }

  makeCategoryChoice = (consentLanguage) => {
    const { stacks } = this.state;
    window.parent.postMessage({
      name: "sp.hideMessage",
      fromPM: true,
      actionType: 'SE',
      consentLanguage,
      payload: {
        lang: consentLanguage,
        categories: stacks.map(c => ({
          _id: c._id,
          consent: c.type === 'CUSTOM' ? Boolean(c.consent) : true,
          legInt: c.type === 'CUSTOM' ? Boolean(c.legInt) : true,
        }))
      }
    }, '*');
  }

  dismissNotice = () => {
    this.makeChoice('Dismiss');
  }

  handleMessages = ({ data }) => {
    if (data.name === 'sp.choiceAction' && data.type) {
      const choice = this.state.choices.find(c => c.type === data.type);
      const noticeComponent = this.state.components ? this.state.components[0] : {};
      if (choice) {
        window.parent.postMessage({
          name: 'sp.hideMessage',
          actions: [{ type: "choice", data: choice }],
          settings: noticeComponent.settings || {}
        }, '*');
      }
    }
  }

  loadPreloadedMsg = ({ origin, data }) => {
    if (!data.fromNativeSDK) { 
      if (msgV2Origins.indexOf(origin) > -1 && window.location.host !== 'localhost') return;
    }
    if (data.name === 'sp.loadMessage') {
      this.loadMessage(data);
    };
  }

  handleDev = (event) => {
    if (event.data && typeof event.data === 'string') {
      const messageData = JSON.parse(event.data);
      if (messageData.components[0].css) addStylesToHead(messageData.components[0].css);
      this.setState({
        devOpts: messageData.devOpts || {},
        components: messageData.components,
        current: messageData.current,
        siteId: messageData.siteId,
        highlight: messageData.highlight,
      });
    }
  }

  noticeSetCurrent = (newCurrent, e) => {
    let target = e.target === e.currentTarget;
    if (e.currentTarget.className.includes('text')) target = true;
    if (e.currentTarget.className.includes('message-stacks')) target = true;
    if (target) {
      window.parent.postMessage({ action: 'dev.setCurrent', newCurrent }, '*')
    }
  }

  updateStacks = (stacks) => this.setState({ stacks })

  doAction = (action) => window.parent.postMessage({ action }, '*')

  render() {
    const noticeComponent = this.state.components ? this.state.components[0] : {};
    const components = noticeComponent.children || [];
    const OriginalSettings = noticeComponent.settings;
    const Settings = new NoticeSettings(noticeComponent.settings || {});
    let noticeClass = this.props.dev ? 'message message-dev' : 'message';

    const styles = {
      ...Settings.padding,
      ...Settings.border
    }, closeStyles = { ...Settings.closeFont }, veilStyles = {};

    styles.background = Settings.background;

    /* WIDTH & HEIGHT */
    const { paddingTop, paddingBottom, paddingLeft, paddingRight } = Settings.padding;
    styles.width = Settings.width.type === 'auto' ? 'auto' : Settings.width.value + Settings.width.type;
    styles.height = Settings.height.type === 'auto' ? 'auto' : Settings.height.value + Settings.height.type;
    if (Settings.height.type === '%') {
      styles.height = `calc(${Settings.height.value}% - ${(paddingTop + paddingBottom)}px`;
    }
    if (!Settings.showVeil && Settings.type === 'bottom' && Settings.height.type === '%' && !this.props.dev) {
      styles.height = '100%';
    }
    if (Settings.width.type === '%' && (Settings.type === 'top' || Settings.type === 'bottom')) {
      styles.width = `calc(${Settings.width.value}% - ${(paddingLeft + paddingRight)}px`;
    }

    /* Close Button */
    if (Settings.showClose) {
      if (Settings.closeAlign === 'left') {
        closeStyles.right = 'auto';
        closeStyles.left = '20px';
        closeStyles.textAlign = 'left';
      } else {
        closeStyles.left = 'auto';
        closeStyles.right = '20px';
        closeStyles.textAlign = 'right';
      }
      closeStyles.zIndex = 2147483646;
    } else {
      closeStyles.display = 'none';
    }

    /* VEIL STYLES */
    veilStyles.display = Settings.showVeil ? 'flex' : 'none';
    veilStyles.background = Settings.veilColor;
    veilStyles.opacity = Settings.veilOpacity;

    const preview = this.props.tiny || this.props.mini || this.props.full;
    const fixed = Settings.type === 'top' || Settings.type === 'bottom';
    const scale = this.props.mini ? 0.4 : (this.props.full ? 1.0 : 0.2);
    if (preview) {
      styles.transform = `scale(${scale}) translateX(-50%) translateY(-50%)`;
      if (fixed) styles.transform = `scale(${scale}) translateX(-50%)`;
      styles.transformOrigin = Settings.type === 'bottom' ? 'left bottom' : 'left top';
      noticeClass = noticeClass + ' message-mini';
    }
    if (preview && (fixed || Settings.width.type !== 'px') && !this.props.full) {
      const previewWidth = this.props.mini ? 364 : 160;
      const percent = fixed || Settings.width.type === 'auto' ? 100 : Settings.width.value;
      const scaledWidth = ((1 / scale) * previewWidth * (100 / percent)) - (paddingLeft + paddingRight);
      styles.width = scaledWidth + 'px';
    }

    if (Settings.type === 'modal') noticeClass += ' type-modal';
    if (Settings.type === 'top') noticeClass += ' type-top';
    if (Settings.type === 'bottom') noticeClass += ' type-bottom';
    if (Settings.type === 'bottom' && !OriginalSettings.border) noticeClass += ' no-border';

    const dev = {
      highlight: this.state.highlight,
      current: JSON.stringify(this.state.current || [0]),
      opts: this.state.devOpts,
      add: () => this.doAction('dev.add'),
      templates: () => this.doAction('dev.templates'),
      hover: {
        onClick: (e) => {
          e.preventDefault();
          const target = e.target && e.target.getAttribute('path') ? JSON.parse(e.target.getAttribute('path')) : null;
          if (target) {
            this.noticeSetCurrent(target, e);
          } else if (!target && e.currentTarget && (e.currentTarget.className.includes('text') || e.currentTarget.className.includes('message-stacks'))) {
            const newTarget = e.currentTarget && e.currentTarget.getAttribute('path') ? JSON.parse(e.currentTarget.getAttribute('path')) : null;
            if (newTarget) this.noticeSetCurrent(newTarget, e);
          }
        },
        onMouseOver: (e) => {
          e.preventDefault();
          let target = e.target;
          if (e.currentTarget.className.includes('text')) target = e.currentTarget;
          if (e.currentTarget.className.includes('message-stacks')) target = e.currentTarget;
          if (target.className.includes('message-component')) target.id = 'on-hover';
        },
        onMouseOut: (e) => {
          e.preventDefault();
          let target = e.target;
          if (e.currentTarget.className.includes('text')) target = e.currentTarget;
          if (e.currentTarget.className.includes('message-stacks')) target = e.currentTarget;
          target.id = '';
        }
      }
    };

    let language;
    let refetchStacks = false;
    if (!Settings.useBrowserDefault && !Settings.supportedLanguages.includes(this.state.stacksLanguage)) {
      language = this.props.consentLanguage || Settings.language || Settings.defaultLanguage;
      refetchStacks = this.state.stacksLanguage && (this.state.stacksLanguage !== language);
    } else {
      language = this.props.consentLanguage || getBrowserLanguage();
      const languageIsSupported = isLangSupported(language, Settings, [noticeComponent]);
      language = languageIsSupported ? language : (Settings.language || Settings.defaultLanguage || 'EN');
    };

    const className = convertClassName(noticeComponent.handle);

    const notice = (
      <div ref={element => this.noticeRef = element} className={noticeClass} style={styles}>
        <div style={closeStyles}>
          <span
            tabIndex="0"
            className="message-stacksclose"
            onClick={this.dismissNotice}
          >
            X
          </span>
        </div>
        {Children({
          path: [0],
          dev: this.props.dev ? dev : null,
          components: components,
          siteId: this.state.siteId,
          message: Settings,
          makeChoice: this.makeChoice,
          updateStacks: this.updateStacks,
          consentUUID: this.props.consentUUID,
          cmpv2Origin: this.props.cmpv2Origin,
          language,
          width: this.state.width,
          preview: preview,
          stacksData: this.state.stacks,
          preloadMsg: this.props.preloadMsg,
        })}
      </div>
    );

    const emptyNotice = (
      <div id="empty-message" role="presentation">
        <button onClick={this.props.dev ? dev.add : null} className="empty-add">+ Add Components</button>
        <div className="empty-or">or</div>
        <button className="empty-template" onClick={this.props.dev ? dev.templates : null}>Start from Template</button>
      </div>
    );

    const tracker = this.state.siteId && this.props.messageId && !this.props.dev ? (
      <div style={{ display: 'none' }}>
        <img src={`https://d2p3zdq8vjvnxd.cloudfront.net/?s_id=${this.state.siteId}&m_id=${this.props.messageId}&consentUUID=${this.props.consentUUID}&requestUUID=${this.props.requestUUID}`} />
      </div>
    ) : null;

    let noticeContainer = (
      <div className={`message-container${className}`}>
        {tracker}
        {Settings.useSafeArea ? '' : <div className="message-overlay" style={veilStyles} />}
        {components.length ? notice : emptyNotice}
      </div>
    );

    noticeContainer = Settings.useSafeArea
      ? <SafeArea safeBottom={Settings.type !== 'bottom'} veil={<div className="message-overlay" style={veilStyles} />}>{noticeContainer}</SafeArea>
      : noticeContainer;

    if (this.state.loading) return (
      <div className="loader-container">
        <div className="loader" />
      </div>
    );
    return (
      <DefaultLanguageContext.Provider value={Settings.language || Settings.defaultLanguage}>
        <RefetchStacksContext.Provider value={refetchStacks}>
          {(this.props.mini || this.props.tiny || this.props.full) ? notice : noticeContainer}
        </RefetchStacksContext.Provider>
      </DefaultLanguageContext.Provider>
    )
  }
}

export default injectQueryParams(Notice);